import React, { createContext, useEffect, useState, useRef } from 'react';
import axios from 'axios';

export const ContentCacheContext = createContext(null);

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';
const API = `${BACKEND_URL}/api`;
const CACHE_KEY = 'showlib_content';
const CACHE_TIME_KEY = 'showlib_content_time';
const CACHE_DURATION = 10 * 60 * 1000; // 10 minutos
const PAGE_SIZE = 10000; // Cargar todos los items de una vez

// Función para comprimir datos (solo mantener campos esenciales)
const compressData = (data) => {
	return (data || []).map(item => ({
		id: item.id,
		title: item.title,
		name: item.name,
		original_title: item.original_title,
		content_type: item.content_type,
		release_date: item.release_date,
		first_air_date: item.first_air_date,
		poster_path: item.poster_path,
		backdrop_path: item.backdrop_path,
		overview: item.overview,
		genres: item.genres,
		countries: item.countries,
		rating: item.rating
	}));
};

// Función para intentar guardar en localStorage, si no cabe, usar sessionStorage
const trySaveToStorage = (key, data) => {
	const json = JSON.stringify(data);
	try {
		localStorage.setItem(key, json);
		return true;
	} catch (e) {
		if (e.name === 'QuotaExceededError') {
			try {
				sessionStorage.setItem(key, json);
				return true;
			} catch (e2) {
				console.warn('Could not save to localStorage or sessionStorage');
				return false;
			}
		}
		return false;
	}
};

// Función para recuperar de storage (intenta localStorage primero, luego sessionStorage)
const getFromStorage = (key) => {
	try {
		let data = localStorage.getItem(key);
		if (data) return JSON.parse(data);
		
		data = sessionStorage.getItem(key);
		if (data) return JSON.parse(data);
		
		return null;
	} catch (e) {
		return null;
	}
};

export function ContentCacheProvider({ children }) {
	// Estado inmediato: intenta cargar del storage primero
	const [contentData, setContentData] = useState(() => {
		const cached = getFromStorage(CACHE_KEY);
		return cached || null;
	});
	
	const [isLoading, setIsLoading] = useState(false);
	const [error, setError] = useState(null);
	const [lastFetchTime, setLastFetchTime] = useState(null);
	const [currentPage, setCurrentPage] = useState(1);
	const [totalPages, setTotalPages] = useState(0);
	const [hasMore, setHasMore] = useState(true);
	const isFetchingRef = useRef(false);
	const retryCountRef = useRef(0);
	const maxRetriesRef = useRef(3);

	// Función para obtener TODO el contenido
	const fetchFirstPage = async (forceRefresh = false) => {
		if (isFetchingRef.current) return;

		const now = Date.now();
		const cachedTime = getFromStorage(CACHE_TIME_KEY);

		// Si no es forzado y tenemos cache válido, usar cache sin fetch
		if (!forceRefresh && cachedTime && now - Number(cachedTime) < CACHE_DURATION) {
			return;
		}

		isFetchingRef.current = true;
		if (!contentData) {
			setIsLoading(true);
		}
		
		try {
			// Cargar TODO el contenido sin paginar
			const response = await axios.get(`${API}/content`, { 
				params: {
					page: 1,
					limit: 10000  // Cargar hasta 10000 items (todo lo disponible)
				},
				timeout: 60000,  // Timeout más largo para cargar todo
			});
			
			let contentArray = response.data;
			if (response.data && response.data.data && Array.isArray(response.data.data)) {
				contentArray = response.data.data;
			} else if (!Array.isArray(response.data)) {
				throw new Error('Respuesta en formato inesperado');
			}
			
			if (!contentArray || contentArray.length === 0) {
				throw new Error('Respuesta vacía');
			}
			
			const compressed = compressData(contentArray);
			const sorted = (compressed || []).sort((a, b) => {
				const dateA = new Date(a.release_date || a.first_air_date || 0);
				const dateB = new Date(b.release_date || b.first_air_date || 0);
				return dateB - dateA;
			});

			if (sorted.length === 0) {
				throw new Error('Datos comprimidos están vacíos');
			}

			// Borrar caché antiguo
			try {
				localStorage.removeItem(CACHE_KEY);
				localStorage.removeItem(CACHE_TIME_KEY);
				sessionStorage.removeItem(CACHE_KEY);
				sessionStorage.removeItem(CACHE_TIME_KEY);
			} catch (e) {
				console.warn('Error limpiando caché:', e);
			}

			// Guardar todo el contenido
			trySaveToStorage(CACHE_KEY, sorted);
			trySaveToStorage(CACHE_TIME_KEY, now);
			
			setContentData(sorted);
			setCurrentPage(1);
			setTotalPages(1);
			setHasMore(false);  // No hay más contenido
			setLastFetchTime(now);
			setError(null);
			
			console.log(`✓ Todo el contenido cargado: ${sorted.length} elementos`);
		} catch (err) {
			console.error('Error fetching content:', err?.message);
			
			const isTimeoutOrNetworkError = err?.code === 'ECONNABORTED' || 
											err?.message?.includes('timeout') || 
											!err?.response;
			
			if (isTimeoutOrNetworkError && retryCountRef.current < maxRetriesRef.current && !contentData) {
				retryCountRef.current++;
				const delayMs = Math.pow(2, retryCountRef.current) * 1000;
				console.log(`Reintentando en ${delayMs}ms`);
				setTimeout(() => {
					isFetchingRef.current = false;
					fetchFirstPage(true);
				}, delayMs);
				return;
			} else {
				retryCountRef.current = 0;
			}
			
			if (!contentData) {
				setError('No se pudo cargar el contenido');
			}
		} finally {
			setIsLoading(false);
			isFetchingRef.current = false;
		}
	};

	// Función para cargar más contenido (no es necesaria si ya se carga todo)
	const loadMoreContent = async () => {
		// Ya todo el contenido está cargado, no se necesita hacer más fetch
		console.log('Todo el contenido ya ha sido cargado');
		return;
	};
			// Precarga en segundo plano usando requestIdleCallback o setTimeout
	useEffect(() => {
		const now = Date.now();
		const cachedTime = getFromStorage(CACHE_TIME_KEY);
		const cacheIsExpired = !cachedTime || now - Number(cachedTime) >= CACHE_DURATION;

		// Siempre hacer fetch si el caché expiró o no existe
		if (cacheIsExpired) {
			// Fetch inmediato sin bloquear UI
			const scheduleImmediateFetch = () => {
				if ('requestIdleCallback' in window) {
					requestIdleCallback(() => {
						fetchFirstPage(true);
					}, { timeout: 1000 });
				} else {
					setTimeout(() => {
						fetchFirstPage(true);
					}, 100);
				}
			};
			scheduleImmediateFetch();
		}

		// Actualizar cada 20 minutos en segundo plano
		const interval = setInterval(() => {
			if ('requestIdleCallback' in window) {
				requestIdleCallback(() => {
					fetchFirstPage(true);
				});
			} else {
				fetchFirstPage(true);
			}
		}, 20 * 60 * 1000);

		// 🔴 ESCUCHAR INVALIDACIONES DE CACHÉ EN TIEMPO REAL (WebSocket y eventos)
		const handleCacheInvalidation = () => {
			console.log('🔄 Event: Content cache invalidated - Refetching immediately...');
			// Reload inmediato cuando se invalida el caché
			if ('requestIdleCallback' in window) {
				requestIdleCallback(() => {
					fetchFirstPage(true);
				}, { timeout: 500 });
			} else {
				setTimeout(() => {
					fetchFirstPage(true);
				}, 100);
			}
		};

		// Escuchar evento de invalidación de caché desde el hook useContentCacheInvalidator
		window.addEventListener('content-cache-invalidated', handleCacheInvalidation);

		return () => {
			clearInterval(interval);
			window.removeEventListener('content-cache-invalidated', handleCacheInvalidation);
		};
	}, []);

	const value = {
		contentData,
		isLoading,
		error,
		lastFetchTime,
		currentPage,
		totalPages,
		hasMore,
		refetchContent: () => fetchFirstPage(true),
		loadMoreContent,  // 🔴 NUEVA FUNCIÓN PARA CARGAR MÁS
	};

	return (
		<ContentCacheContext.Provider value={value}>
			{children}
		</ContentCacheContext.Provider>
	);
}

export function useContentCache() {
	const context = React.useContext(ContentCacheContext);
	if (!context) {
		throw new Error('useContentCache debe ser usado dentro de ContentCacheProvider');
	}
	return context;
}
